import { Download, Expand, Upload } from "lucide-react";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";

interface ToolbarButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

function ToolbarButton({ icon, label, onClick, disabled }: ToolbarButtonProps) {
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer disabled:opacity-50"
          aria-label={label}
        >
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

interface ArtifactToolbarActionsProps {
  /** Called when the download button is clicked. */
  onDownload?: () => void;
  /** Called when the export-to-workbench button is clicked. */
  onExport?: () => void;
  /** Called when the fullscreen button is clicked. */
  onFullscreen?: () => void;
}

/**
 * Shared toolbar action buttons for artifact message components.
 * Renders download, export, and fullscreen buttons with
 * consistent styling and layout.
 *
 * Download and export-to-workbench are hidden in shared-conversation
 * views, where the viewer typically does not own the underlying dataset
 * or workbench.
 */
export function ArtifactToolbarActions({
  onDownload,
  onExport,
  onFullscreen,
}: ArtifactToolbarActionsProps) {
  const { appContext } = useAssistantContext();
  const isSharedView = appContext === "shared";

  return (
    <div className="ml-auto flex items-center">
      {onDownload && !isSharedView && (
        <ToolbarButton
          icon={<Download className="size-3.5" />}
          label="Download"
          onClick={onDownload}
        />
      )}
      {onExport && !isSharedView && (
        <ToolbarButton
          icon={<Upload className="size-3.5" />}
          label="Export to workbench"
          onClick={onExport}
        />
      )}
      {onFullscreen && (
        <ToolbarButton
          icon={<Expand className="size-3.5" />}
          label="View in full screen"
          onClick={onFullscreen}
        />
      )}
    </div>
  );
}
